// filepath: apps/web/src/components/AppShell/AppShell.tsx
//
// Two-region layout chrome for authenticated OSD routes:
//   ┌─────────────────────────────────────┐
//   │ Top header (brand + sign-out)       │
//   ├──────────────┬──────────────────────┤
//   │              │                      │
//   │ Sidebar nav  │ Main content         │
//   │              │ ({children})         │
//   │              │                      │
//   └──────────────┴──────────────────────┘
//
// Public routes (/login, /register) bypass the shell — the route
// file renders bare <Outlet/> instead of <AppShell>.
//
// Below the md breakpoint the sidebar hides and the MobileDrawer
// (rendered next to <main>) takes over. The header hamburger
// toggles it. See root/osd#33.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { Menu } from '@osd/icons/menu';
import { PanelLeft } from '@osd/icons/panel-left';
import { Button } from '@osd/ui/button';
import { Heading } from '@osd/ui/heading';
import { Pill } from '@osd/ui/pill';
import { Sidebar } from '@osd/ui/nav';
import { Stack } from '@osd/ui/stack';
import { ThemeToggle } from '@osd/ui/theme-toggle';
import { useAuth } from '../../features/auth/AuthProvider';
import { useNavSections } from '../../features/nav/useNavSections';
import { useTheme } from '../../lib/theme';
import { MobileDrawer, MOBILE_DRAWER_ID } from './MobileDrawer';

/** Paths that intentionally render WITHOUT the AppShell chrome. */
const PUBLIC_PATHS: ReadonlySet<string> = new Set(['/login', '/register']);

export interface AppShellProps {
  readonly children: ReactNode;
}

export function AppShell({ children }: AppShellProps): JSX.Element | null {
  const auth = useAuth();
  // useLocation's reactive subscription matters here: when the
  // router transitions from /login to a protected route right after
  // login(), location.pathname flips synchronously and this guard
  // exits BEFORE auth.user would have a chance to mount the shell
  // over the still-mounted login route.
  const location = useLocation();

  // Hooks MUST be called before any conditional return (rules-of-hooks).
  // Order: useAuth → useTheme → useNavSections → useState → useRef →
  // useCallback, then a single render-time null-return guard.
  const sections = useNavSections(auth.user?.isAdmin === true);
  // ThemeToggle state lives in the header so the sun/moon glyph
  // stays in sync with the rest of the page. `useTheme()` returns
  // stable callbacks; safe to consume in deps arrays elsewhere.
  const { resolved: themeResolved, toggle: toggleTheme } = useTheme();

  // Mobile drawer — closed by default; toggled via the header
  // hamburger button. MobileDrawer closes itself on route change
  // (TanStack Router useLocation subscription).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Sidebar icon-rail state (desktop). The collapsed sidebar is a
  // ~3rem wide rail showing only section + link icons. Hover /
  // focus temporarily expands to full width (CSS — see
  // `_sidebar.scss` in @osd/ui). The toggle button inside
  // <Sidebar> drives this via the controlled `onCollapsedChange`
  // callback. We don't add a duplicate header toggle — the one
  // inside <Sidebar> is the canonical control.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Docs-route detection. Cheap to compute every render; consumed
  // both by the JSX and by the overlay-drawer hooks below.
  const isDocsRoute =
    location.pathname.startsWith('/ui') || location.pathname.startsWith('/icons');

  // Overlay-drawer state for the app sidebar on docs routes
  // (desktop). Closed by default so the docs nav is the only
  // visible chrome until the user clicks the header toggle. On
  // non-docs routes this state is irrelevant (the sidebar is
  // rendered inline).
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false);
  const openDocsDrawer = useCallback(() => setDocsDrawerOpen(true), []);
  const closeDocsDrawer = useCallback(() => setDocsDrawerOpen(false), []);

  // Close the overlay drawer on route change so a tapped link
  // doesn't leave it open over the new page.
  useEffect(() => {
    setDocsDrawerOpen(false);
  }, [location.pathname]);

  // Close on Escape (desktop parity with MobileDrawer).
  useEffect(() => {
    if (!docsDrawerOpen) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setDocsDrawerOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [docsDrawerOpen]);

  // Public-route guard: when on /login or /register, never render
  // the shell — even if auth.user is briefly set (e.g. right after
  // login() succeeds but before navigate('/...') commits). This
  // closes the bleed-through race where AppShell + the login form
  // mount simultaneously for a single React commit. (root/osd#97)
  // NB: every hook above MUST stay above this point — see the
  // rules-of-hooks guard the lint config enforces.
  if (PUBLIC_PATHS.has(location.pathname)) return null;

  // Docs-surface routes (/ui/* + /icons/*) used to hide the app
  // sidebar entirely (commit f18a735). That was a usability
  // regression: the docs layout has its own narrow side nav with
  // component links, but users on a docs page had NO way to reach
  // /users, /admin, or back to / without first clicking home.
  // Fix: render the app sidebar on docs routes as a SLIDE-IN
  // OVERLAY DRAWER anchored to the left edge with a backdrop.
  // Desktop users get a header button (PanelLeft toggle) to open
  // it; mobile users keep the existing hamburger + MobileDrawer
  // pattern. This keeps the docs layout's narrow side nav visible
  // by default and restores a one-click escape hatch. root/osd#126.

  // Logged-out guard: when AuthProvider's initial /me probe has
  // finished AND user is still null, render nothing. The
  // ProtectedRoute wrapper in __root.tsx observes the same
  // signal and navigates to /login, so this is just a visual
  // guarantee that no chrome leaks. While loading, also render
  // nothing — the brief blank is preferable to flashing the
  // empty AppShell before the user lands.
  if (auth.user === null) return null;
  const isAdmin = auth.user.isAdmin === true;

  return (
    <div className="osd-app-shell">
      <header className="osd-app-shell__header">
        <Stack
          direction="row"
          align="center"
          justify="between"
          className="osd-app-shell__header-row"
        >
          <Stack direction="row" align="center" gap={2}>
            <button
              ref={toggleRef}
              type="button"
              className="osd-app-shell__menu-toggle"
              aria-controls={MOBILE_DRAWER_ID}
              aria-expanded={drawerOpen}
              aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'}
              onClick={drawerOpen ? closeDrawer : openDrawer}
            >
              <Menu aria-hidden="true" />
            </button>
            <Link to="/" className="osd-app-shell__brand">
              <Heading level={4}>OSD</Heading>
            </Link>
            {isDocsRoute && (
              <button
                type="button"
                className="osd-app-shell__sidebar-toggle"
                aria-label="Show app navigation"
                aria-expanded={docsDrawerOpen}
                onClick={docsDrawerOpen ? closeDocsDrawer : openDocsDrawer}
              >
                <PanelLeft aria-hidden="true" />
              </button>
            )}
          </Stack>
          {auth.user !== null ? (
            <Stack direction="row" align="center" gap={2}>
              <Pill variant="neutral" size="sm">
                {auth.user.name}
              </Pill>
              {isAdmin ? (
                <Pill variant="accent" size="sm">
                  admin
                </Pill>
              ) : null}
              <ThemeToggle resolved={themeResolved} onClick={toggleTheme} />
              <Button variant="ghost" onClick={() => void auth.logout()}>
                Sign out
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </header>
      <div className="osd-app-shell__body">
        {isDocsRoute ? (
          <>
            {docsDrawerOpen && (
              <div
                className="osd-app-shell__backdrop"
                onClick={closeDocsDrawer}
                aria-hidden="true"
              />
            )}
            <aside
              className={`osd-app-shell__sidebar-overlay${docsDrawerOpen ? ' osd-app-shell__sidebar-overlay--open' : ''}`}
              aria-label="App navigation"
            >
              <Sidebar
                sections={sections}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
              />
            </aside>
          </>
        ) : (
          <Sidebar
            sections={sections}
            collapsed={sidebarCollapsed}
            onCollapsedChange={setSidebarCollapsed}
          />
        )}
        <main className="osd-app-shell__main">{children}</main>
        <MobileDrawer
          open={drawerOpen}
          onClose={closeDrawer}
          sections={sections}
          toggleRef={toggleRef}
        />
      </div>
    </div>
  );
}
